import React, { FunctionComponent } from 'react';

import dayjs from 'dayjs';
import Link from 'next/link';
import { AiOutlineRise } from 'react-icons/ai';
import { RiAlarmWarningLine } from 'react-icons/ri';

import { Itransaction } from '@/modules/core/types';
import { TkTayaraToken } from '@/tayara-kit/icons';
import Text from '@/tayara-kit/Text';

import DownloadReceipts from './DownloadReceipts';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  state: boolean;
}

export interface TransactionHistoryTabProps {
  balance: number;
  transactionList: Itransaction[];
}

const Badge: FunctionComponent<BadgeProps> = ({
  state,
  children,
  ...props
}) => (
  <span
    {...props}
    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5
    ${state ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
  >
    {children}
  </span>
);
const TransactionHistoryTab = ({
  balance,
  transactionList,
}: TransactionHistoryTabProps) => {
  const targetDate = new Date();
  const filtredTransactions = transactionList
    .filter(
      (trc) =>
        trc && trc.description && trc.description.startsWith('Expire le:')
    )
    .filter((trc) => {
      if (trc && trc.description) {
        const description = trc.description
          .replace('Expire le:', '')
          .split('|')?.[0]
          ?.trim();
        const dateComponents = description?.split('-');
        if (dateComponents && dateComponents.length === 3) {
          const day = Number(dateComponents[0]);
          const month = Number(dateComponents[1]);
          const year = Number(dateComponents[2]);
          if (
            !Number.isNaN(day) &&
            !Number.isNaN(month) &&
            !Number.isNaN(year)
          ) {
            const expirationDate = new Date(year, month - 1, day);
            if (!Number.isNaN(expirationDate.getTime())) {
              if (expirationDate >= targetDate) {
                return trc;
              }
            }
          }
        }
      }
      return false;
    });

  return (
    <div className="w-full">
      <Text
        as="h2"
        text={`Mes transactions`}
        className="text-2xl text-gray-600 font-bold mb-4"
      />
      <div className="w-full overflow-auto">
        <div className="w-full flex flex-wrap gap-4  mb-4 my-4">
          <div className="w-full lg:w-fit justify-evenly grow stats flex flex-col  text-gray-700 border-[1px] border-gray-200  rounded-lg shadow-sm ">
            <div className="stat">
              <div className="w-full flex justify-between text-lg font-semibold text-gray-800/70 mb-2">
                <Text text="Solde des Jetons" />
                <TkTayaraToken size={30} className="text-gray-600" />
              </div>
              <div
                className={`w-full flex flex-col md:flex-row  flex-wrap gap-y-3 items-center justify-center md:justify-between mt-4 mx-auto stat-value text gap-x-2 text-gray-600`}
              >
                {balance}
                <div className="w-fit flex items-center">
                  <Link
                    href={{ pathname: '/buy-tokens' }}
                    passHref
                    aria-label="Acheter de t-tokens"
                    className="w-full md:w-fit lg:w-fit mx-auto"
                  >
                    <button className="w-full md:w-fit lg:w-fit flex flex-col mx-auto rounded-xl border-[1.5px] border-gray-400 bg-gradient-to-r from-blue-700 via-blue-500 to-blue-400 !text-white hover:from-blue-700 hover:to-blue-600 group shadow-sm  hover:shadow-none">
                      <div className="flex gap-x-4 mx-auto items-center">
                        <div className="flex flex-col my-3">
                          <span className="flex items-center text-base font-semibold mx-8 lg:mx-10 leading-6">
                            Achetez des <TkTayaraToken className="mx-2" />
                            jetons
                          </span>
                        </div>
                      </div>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <Text
              className=" justify-self-center my-4 ml-6 mr-auto w-fit text-gray-700 font-light leading-tight text-base"
              text="Votre solde total de jetons"
            />
          </div>

          <div className="w-fit lg:w-[30rem] justify-evenly grow stats flex flex-col  text-gray-700 border-[1px] border-gray-200  rounded-lg shadow-sm ">
            <div className="stat">
              <div className="flex justify-between text-lg font-semibold text-gray-800/70 mb-2">
                <Text text="Boostez vos annonces" />
                <AiOutlineRise
                  className="group-hover:animate-rocking text-gray-600"
                  size={30}
                />{' '}
              </div>
              <div className="mt-4 stat-value flex items-center justify-center text-gray-600">
                <div className="flex w-full justify-center md:justify-center lg:justify-center items-center ">
                  <Link
                    href={{ pathname: '/profile', query: { t: 'myListings' } }}
                    className=""
                    passHref
                  >
                    <button className="w-full md:w-fit lg:w-fit mx-auto flex flex-col items-center justify-center  rounded-xl border-[1.5px] border-yellow-300 bg-gradient-to-tr from-yellow-300 via-orange-400 to-orange-300 text-white text-base font-extrabold hover:from-yellow-500 hover:to-orange-500 group shadow-sm  hover:shadow-none">
                      <div className=" flex mx-5 md:mx-32 lg:mx-28 xl:mx-20 gap-x-5 md:gap-x-6 lg:gap-x-2 items-center">
                        <AiOutlineRise
                          className="group-hover:animate-rocking mt-1"
                          size={38}
                        />
                        <div className="w-full mx-auto flex flex-col my-3 pr-4 md:pr-0">
                          <span className="text-base font-semibold leading-6">
                            Boostez mes annonces
                          </span>
                          <span className="tracking-widest uppercase text-xs font-normal leading-4 ">{`" Bi3 Fissa3 "`}</span>
                        </div>
                      </div>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <Text
              className=" w-5/6 justify-self-center my-4 ml-6 mr-auto text-gray-700 font-light leading-tight text-base   "
              text="Choisissez une annonce, boostez là et elle aura 2 à 7 fois plus de visibilité qu'une annonce simple"
            />
          </div>

          {filtredTransactions.length > 0 && (
            <div className="w-full  justify-evenly lg:w-[30rem] grow stats flex flex-col  text-gray-700 border-[1px] border-gray-200  rounded-lg shadow-sm ">
              <div className="stat">
                <div className="flex justify-between text-lg font-semibold text-gray-800/70 mb-2">
                  <Text text="Expiration des Jetons" />
                  <RiAlarmWarningLine size={30} className="text-gray-600" />
                </div>
                <div className="w-full overflow-x-hidden mt-4 mr-0 lg:mr-10 border-b-[1px] border-gray-300 stat-value flex items-center text gap-x-2 text-gray-600">
                  <div
                    className={`w-full ${
                      filtredTransactions.length >= 2 ? 'h-[4rem]' : 'h-[3rem]'
                    } overflow-x-hidden overflow-y-visible`}
                  >
                    <table className="">
                      <tbody>
                        {filtredTransactions.map((trc) => (
                          <tr
                            key={JSON.stringify(trc)}
                            className="flex items-center gap-x-2"
                          >
                            <td
                              className="flex gap-2 items-center "
                              tabIndex={0}
                            >
                              <TkTayaraToken className="text-info" size={20} />
                              <Badge state={trc.isVirement || true}>
                                {trc.amount}
                              </Badge>
                            </td>
                            <td tabIndex={0}>
                              <div className="text-xs lg:text-sm font-semibold">
                                {trc.description}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Text
                className=" justify-self-center my-4 ml-6 mr-auto w-fit text-gray-700 font-light leading-tight text-base   "
                text="Récapitulatif des dates d'expiration de vos jetons"
              />
            </div>
          )}

          {/* Page actions */}
        </div>
      </div>
      <div className="w-full flex flex-wrap justify-between my-4 ml-2 lg:ml-1 ">
        <Text
          as="h2"
          text={`Tableau des transactions`}
          className="text-lg text-gray-600 font-bold mb-4"
        />
        <DownloadReceipts transactionList={transactionList} />
      </div>
      <div className="overflow-x-auto border-[1px] border-gray-200 rounded-lg shadow mt-4">
        <table className="table w-full h-full">
          <tr tabIndex={0} className="border-b-[1px] border-gray-300">
            <th tabIndex={0}>Date</th>
            <th tabIndex={0}>Montant</th>
            <th tabIndex={0}>Description </th>
          </tr>

          <thead />
          <tbody>
            {transactionList.map((trc) => (
              <tr
                key={JSON.stringify(trc)}
                className="w-full border-b-[1.1px] border-gray-200"
              >
                <td tabIndex={0} className="w-3/3">
                  <div className="flex flex-col">
                    <span className="text-2xs lg:text-sm font-normal text-gray-600">
                      {dayjs(trc.createdTime).format('LLL')}
                    </span>
                  </div>
                </td>
                <td
                  className="flex gap-2 items-center h-[8rem] lg:h-[4rem]"
                  tabIndex={0}
                >
                  <TkTayaraToken className="text-info" size={20} />
                  <Badge state={trc.isVirement || true}>
                    {trc.isVirement ? `+` : `-`}
                    {trc.amount}
                  </Badge>
                </td>
                <td tabIndex={0} className="w-2/3">
                  <div className="text-2xs lg:text-sm text-gray-600 max-w-full whitespace-normal">
                    {trc.description}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot></tfoot>
        </table>
      </div>
    </div>
  );
};

export default TransactionHistoryTab;
